/**
 * Состояние Mini App: профиль, баланс, токены и текущая вкладка.
 * Источник данных — наш REST (STEP 11), никакой отдельной логики на фронте.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '../lib/api.js';
import type { Me } from '../lib/api.js';
import { expandViewport, telegramEnv } from '../lib/telegram.js';

export type TabId = 'home' | 'trade' | 'tokens' | 'account';

interface AppState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  tab: TabId;
  toast: string | null;
  setTab: (tab: TabId) => void;
  refresh: () => Promise<void>;
  showToast: (message: string) => void;
  /** Точечное обновление после сделки/покупки — без лишнего запроса. */
  patchMe: (patch: Partial<Me>) => void;
  /**
   * Перевод по ключу общего словаря.
   * Пока словарь не загрузился, возвращается `fallback` — показывать
   * пользователю сырой ключ вроде `app.error.unauthorized` нельзя.
   */
  t: (key: string, params?: Record<string, string | number>, fallback?: string) => string;
  /** Перезагрузка словаря после смены языка. */
  reloadTexts: (locale?: string) => Promise<void>;
  /** Путь к картинке: из админки, иначе файл из сборки. */
  art: (key: string, fallback: string) => string;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * Ошибка живёт ключом словаря, а не готовой строкой.
   *
   * Экран ошибки успевает отрисоваться раньше, чем приедет `/api/texts`, и
   * замороженная строка так и осталась бы на языке запасного текста. Ключ же
   * переводится на каждом рендере — как только словарь загрузился, надпись
   * сама встаёт на нужный язык.
   */
  const [errorKey, setErrorKey] = useState<{ key: string; fallback: string } | null>(null);
  const [tab, setTab] = useState<TabId>('home');
  const [toast, setToast] = useState<string | null>(null);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [assets, setAssets] = useState<Record<string, string>>({});

  const reloadTexts = useCallback(async (locale?: string) => {
    try {
      const result = await api.texts(locale);
      setTexts(result.texts);
      // картинки тоже бывают локализованные (баннеры с текстом на языке гео):
      // карта ассетов перезагружается вместе со словарём
      const media = await api.media(result.locale);
      setAssets(media.media);
    } catch {
      // без словаря интерфейс покажет ключи — это лучше, чем пустой экран
    }
  }, []);

  /** Подстановка {{placeholder}} — как в i18n ядра. */
  const t = useCallback(
    (key: string, params: Record<string, string | number> = {}, fallback?: string) => {
      const template = texts[key] ?? fallback ?? key;
      return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, name: string) => {
        const value = params[name];
        return value === undefined ? match : String(value);
      });
    },
    [texts],
  );

  /** Замена в админке важнее файла из сборки — так меняем графику без релиза. */
  const art = useCallback(
    (key: string, fallback: string) => assets[key] ?? fallback,
    [assets],
  );

  const refresh = useCallback(async () => {
    try {
      setErrorKey(null);
      setMe(await api.me());
    } catch (err) {
      setErrorKey(
        err instanceof ApiError && err.isUnauthorized
          ? { key: 'app.error.unauthorized', fallback: 'Open the app from Telegram' }
          : { key: 'app.error.load_failed', fallback: 'Failed to load data' },
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const error = useMemo(
    () => (errorKey ? t(errorKey.key, {}, errorKey.fallback) : null),
    [errorKey, t],
  );

  const showToast = useCallback((message: string) => {
    setToast(message);
    globalThis.setTimeout(() => setToast(null), 2600);
  }, []);

  const patchMe = useCallback((patch: Partial<Me>) => {
    setMe((current) => (current ? { ...current, ...patch } : current));
  }, []);

  useEffect(() => {
    expandViewport();
    void refresh();
    // язык берём у Telegram: без него словарь приезжает на языке по умолчанию,
    // и англоязычный пользователь видит русский интерфейс
    // словарь и карта ассетов приезжают вместе (внутри reloadTexts)
    void reloadTexts(telegramEnv().languageCode ?? undefined);
  }, [refresh, reloadTexts]);

  /**
   * Язык из профиля важнее языка Telegram: пользователь мог сменить его в боте,
   * и Mini App обязан показать тот же интерфейс, что и бот.
   */
  const profileLanguage = me?.user.language ?? null;
  useEffect(() => {
    if (profileLanguage && profileLanguage !== telegramEnv().languageCode) {
      void reloadTexts(profileLanguage);
    }
  }, [profileLanguage, reloadTexts]);

  const value = useMemo(
    () => ({
      me,
      loading,
      error,
      tab,
      toast,
      setTab,
      refresh,
      showToast,
      patchMe,
      t,
      reloadTexts,
      art,
    }),
    [me, loading, error, tab, toast, refresh, showToast, patchMe, t, reloadTexts, art],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp вне AppProvider');
  return ctx;
}

/** Профиль гарантированно загружен — для экранов внутри шелла. */
export function useMe(): Me {
  const { me } = useApp();
  if (!me) throw new Error('Профиль ещё не загружен');
  return me;
}
